/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * RLeanView — R-Lean Targeting deep dive.
 *
 * Precinct-level R-lean × stage-1 p_turnout, ranked. This is the "D1 Tier 1
 * stopgap" for coordinators to pick high-priority targeting precincts.
 *
 * Spec: session 5.8 Agent A deliverables.
 *
 * Layout:
 *   [H1: R-Lean Targeting] [subtitle]
 *   [Filter strip: election date, precinct num, min_turnout, limit]
 *   [Audit-log warning banner if limit >= 25]
 *   [Data table: Name | Address | Precinct | p_turnout | r_lean | composite_score]
 *   [Loading/Empty/Error states]
 *
 * This view consumes GET /predictions/r-lean backend endpoint.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { fetchRLean, formatPredictionsError } from '@/api/predictions';
import type { RLeanRow, RLeanResponse } from '@/api/predictions';
import { InfoTip } from '@/components/InfoTip';
import { OnboardingModal, OnboardingHelpButton } from '@/components/OnboardingModal';
import FilterSelect from '@/components/filters/FilterSelect';
import { ViewHeader } from '@/components/ViewHeader';
import client from '@/api/client';

// ============================================================================
// Types
// ============================================================================

interface FilterState {
  electionDate: string;
  electionType: string;
  precinctNum: number | null;
  minTurnout: number;
  limit: number;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_ELECTION_DATE = '2026-08-04'; // Upcoming primary
const DEFAULT_ELECTION_TYPE = 'primary';
const DEFAULT_MIN_TURNOUT = 0.5;
const DEFAULT_LIMIT = 25;

const LIMIT_OPTIONS = [
  { value: '25', label: '25 rows' },
  { value: '100', label: '100 rows' },
  { value: '500', label: '500 rows' },
  { value: '5000', label: 'All' },
];

// ============================================================================
// RLeanView Component
// ============================================================================

export function RLeanView({ onBack }: { onBack?: () => void }) {
  // ============================================================================
  // State: filters
  // ============================================================================
  const [filters, setFilters] = useState<FilterState>({
    electionDate: DEFAULT_ELECTION_DATE,
    electionType: DEFAULT_ELECTION_TYPE,
    precinctNum: null,
    minTurnout: DEFAULT_MIN_TURNOUT,
    limit: DEFAULT_LIMIT,
  });

  // ============================================================================
  // State: data loading
  // ============================================================================
  const [data, setData] = useState<RLeanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forceOnboard, setForceOnboard] = useState(false);

  // ============================================================================
  // State: dropdowns (load precinct options)
  // ============================================================================
  const [precinctOptions, setPrecinctOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // ============================================================================
  // State: privacy policy
  // ============================================================================
  const [kAnonFloor, setKAnonFloor] = useState<number>(5);
  const [privacyError, setPrivacyError] = useState(false);

  // ============================================================================
  // Load privacy policy on mount
  // ============================================================================
  useEffect(() => {
    async function loadPrivacyPolicy() {
      try {
        const res = await client.get<{ r_lean?: number; predictions?: number }>('/meta/privacy');
        // Use 'r_lean' or fallback to 'predictions' or default to 5
        const floor = res.data.r_lean || res.data.predictions || 5;
        setKAnonFloor(floor);
      } catch (err) {
        console.error('Failed to load privacy policy:', err);
        setPrivacyError(true);
        setKAnonFloor(5);
      }
    }

    loadPrivacyPolicy();
  }, []);

  // ============================================================================
  // Load precinct options on mount
  // ============================================================================
  useEffect(() => {
    async function loadPrecinctOptions() {
      try {
        setLoadingOptions(true);
        const res = await client.get('/voters/by-precinct', {
          params: { page_size: 500 },
        });
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data?.items ?? []);
        const opts = rows.map((p: any) => ({
          value: String(p.precinct_num),
          label: `P${p.precinct_num} — ${p.precinct_name || ''}`.trim(),
        }));
        setPrecinctOptions(opts);
      } catch (err) {
        console.error('Failed to load precinct options:', err);
      } finally {
        setLoadingOptions(false);
      }
    }

    loadPrecinctOptions();
  }, []);

  // ============================================================================
  // Fetch R-lean data when filters change
  // ============================================================================
  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchRLean({
          electionDate: filters.electionDate,
          electionType: filters.electionType,
          precinctNum: filters.precinctNum,
          minTurnout: filters.minTurnout,
          limit: filters.limit,
        });

        if (!cancelled) {
          setData(response.items || []);
          setLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        const errMsg = formatPredictionsError(err);
        setError(errMsg);
        setLoading(false);
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [filters]);

  // ============================================================================
  // Helper: format percentage
  // ============================================================================
  function formatPct(val: number): string {
    return `${(val * 100).toFixed(1)}%`;
  }

  // ============================================================================
  // Render: loading state
  // ============================================================================
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-border-base bg-bg-raised/60 p-12 text-center">
          <div className="inline-block h-3 w-3 rounded-full bg-emerald-500 animate-pulse mr-2" />
          <span className="text-fg-secondary">
            Loading R-lean targeting data...
          </span>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render: error state
  // ============================================================================
  if (error) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-status-danger bg-status-danger-bg/40 p-6 text-status-danger">
          <div className="font-semibold mb-1">Failed to load R-Lean data</div>
          <div className="text-sm text-status-danger/80">{error}</div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render: main view
  // ============================================================================
  const showAuditBanner = filters.limit >= 25;
  const minTurnoutSliderValue = Math.round(filters.minTurnout * 100);

  return (
    <div className="space-y-6">
      {/* Onboarding modal */}
      <OnboardingModal
        viewKey="r_lean_targeting"
        title="R-Lean Targeting"
        intro="Precinct R-lean × turnout probability, Tier 1 stopgap. Use this view to identify high-priority targeting precincts where Republican lean is strong and turnout is likely."
        termIds={[
          'r_lean_targeting',
          'precinct',
          'turnout',
        ]}
        workflows={[
          'Filter to upcoming election date and election type.',
          'Adjust minimum turnout probability to focus on reliable voters.',
          'Sort by composite_score (highest first) to find best targets.',
        ]}
        forceOpen={forceOnboard}
        onClose={() => setForceOnboard(false)}
      />

      {/* Header strip */}
      <ViewHeader
        title="R-Lean Targeting"
        tagline="Precinct R-lean × turnout probability, Tier 1 stopgap."
        tone="partisan-r"
        inline={<InfoTip termId="r_lean_targeting" />}
        actions={<OnboardingHelpButton onClick={() => setForceOnboard(true)} />}
      />

      {/* Audit-log warning banner */}
      {showAuditBanner && (
        <div className="rounded-lg border border-status-warning/50 bg-status-warning-bg/30 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="text-status-warning text-lg leading-none mt-0.5">⚠</div>
            <div>
              <div className="text-status-warning text-sm font-semibold">Audit Logging</div>
              <div className="text-status-warning/80 text-sm mt-1">
                Pulls above 25 rows are audit-logged.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy banner */}
      <div className="bg-status-info-bg/20 border border-status-info rounded-lg p-4 flex gap-3 items-start">
        <div className="text-status-info text-lg flex-shrink-0">ℹ</div>
        <p className="text-sm text-status-info">
          {!privacyError
            ? `Rows with fewer than ${kAnonFloor} voters are hidden to protect individual privacy.`
            : 'Rows with fewer than 5 voters are hidden to protect individual privacy.'}
        </p>
      </div>

      {/* Filter strip */}
      <div className="bg-bg-raised/50 border border-border-base rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Election date picker */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="r-lean-election-date"
              className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold"
            >
              Election Date
            </label>
            <input
              id="r-lean-election-date"
              type="date"
              value={filters.electionDate}
              onChange={(e) =>
                setFilters({ ...filters, electionDate: e.target.value })
              }
              className="bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            />
          </div>

          {/* Election type select */}
          <FilterSelect
            label="Election Type"
            value={filters.electionType}
            options={[
              { value: 'primary', label: 'Primary' },
              { value: 'general', label: 'General' },
              { value: 'special', label: 'Special' },
            ]}
            onChange={(val) =>
              setFilters({ ...filters, electionType: val || DEFAULT_ELECTION_TYPE })
            }
          />

          {/* Precinct select */}
          <FilterSelect
            label="Precinct"
            value={filters.precinctNum !== null ? String(filters.precinctNum) : null}
            options={precinctOptions}
            placeholder="All precincts"
            onChange={(val) =>
              setFilters({
                ...filters,
                precinctNum: val ? parseInt(val, 10) : null,
              })
            }
          />

          {/* Limit select */}
          <FilterSelect
            label="Row Limit"
            value={String(filters.limit)}
            options={LIMIT_OPTIONS}
            onChange={(val) =>
              setFilters({ ...filters, limit: parseInt(val || '25', 10) })
            }
          />
        </div>

        {/* Min turnout slider */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label
              htmlFor="r-lean-min-turnout"
              className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold"
            >
              Min. Turnout Probability
            </label>
            <span className="text-sm text-fg-secondary font-mono">
              {formatPct(filters.minTurnout)}
            </span>
          </div>
          <input
            id="r-lean-min-turnout"
            type="range"
            min="0"
            max="100"
            step="5"
            value={minTurnoutSliderValue}
            onChange={(e) => {
              setFilters({
                ...filters,
                minTurnout: parseInt(e.target.value, 10) / 100,
              });
            }}
            className="w-full h-2 bg-bg-elevated rounded-lg appearance-none cursor-pointer accent-emerald-500"
          />
          <div className="flex justify-between text-[10px] text-fg-muted">
            <span>0%</span>
            <span>100%</span>
          </div>
        </div>
      </div>

      {/* Data table */}
      <div className="rounded-lg border border-border-base overflow-hidden min-w-0">
        {data.length === 0 ? (
          <div className="bg-bg-raised/30 p-8 text-center text-fg-muted">
            <div className="text-sm">
              No precinct election results available — contact admin to load
              <code className="bg-bg-base px-2 py-1 rounded mx-1 text-xs">
                precinct_election_results
              </code>
              data.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-bg-raised border-b border-border-base">
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-fg-secondary font-semibold">
                  Voter Name
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-fg-secondary font-semibold">
                  Address
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-fg-secondary font-semibold">
                  Precinct
                </th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-fg-secondary font-semibold">
                  Turnout Prob. <InfoTip termId="turnout_probability" compact />
                </th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-fg-secondary font-semibold">
                  R-Lean <InfoTip termId="r_lean_targeting" compact />
                </th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-fg-secondary font-semibold">
                  Score
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr
                  key={row.voter_id}
                  className={`border-b border-border-base/50 ${
                    idx % 2 === 0 ? 'bg-bg-raised/20' : 'bg-bg-base/10'
                  } hover:bg-bg-raised/50 transition`}
                >
                  <td className="px-4 py-3 text-sm text-fg-primary">
                    {row.first_name} {row.last_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-fg-secondary">
                    {row.address}
                  </td>
                  <td className="px-4 py-3 text-sm text-fg-secondary text-center">
                    P{row.precinct_num}
                  </td>
                  <td className="px-4 py-3 text-sm text-fg-secondary text-right font-mono">
                    {formatPct(row.p_turnout)}
                  </td>
                  <td className="px-4 py-3 text-sm text-fg-secondary text-right font-mono">
                    {row.r_lean.toFixed(3)}
                  </td>
                  <td className="px-4 py-3 text-sm text-fg-primary text-right font-bold">
                    {row.composite_score.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Results count */}
      {data.length > 0 && (
        <div className="text-sm text-fg-muted">
          Showing {data.length} result{data.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
